/**
 * LEOS Mission Ready Navigation Module
 * Handles all navigation-related methods and section displays
 */

export class NavigationModule {
    constructor(mainInterface) {
        this.mainInterface = mainInterface;
    }

    showSection(sectionKey) {
        console.log('showSection called with:', sectionKey);
        
        // If switching away from space-domain, hide the SDA visualization
        if (this.mainInterface.currentSection === 'space-domain' && sectionKey !== 'space-domain') {
            // Use the SDA controller to properly hide everything
            if (window.sdaController && window.sdaController.isVisible) {
                console.log('Hiding SDA visualization');
                window.sdaController.toggle();
            }
        }
        
        // If switching away from mission control, preserve the canvas
        if (this.mainInterface.currentSection === 'mission-control' && sectionKey !== 'mission-control') {
            const canvas = document.getElementById('renderCanvas');
            const originalContainer = document.getElementById('renderCanvas-container');
            
            if (canvas && originalContainer && !originalContainer.contains(canvas)) {
                // Move canvas back to original container
                originalContainer.appendChild(canvas);
                canvas.style.width = '100%';
                canvas.style.height = '100%';
            }
        }
        
        // Update active navigation
        document.querySelectorAll('.nav-item').forEach(item => {
            item.classList.toggle('active', item.dataset.section === sectionKey);
        });
        
        this.mainInterface.currentSection = sectionKey;
        const section = this.mainInterface.sections[sectionKey];
        
        // Update top bar
        document.getElementById('section-title').textContent = section.label;
        document.getElementById('section-subtitle').textContent = this.getSectionSubtitle(sectionKey);
        
        // Load section content
        const contentArea = document.getElementById('content-area');
        
        switch(sectionKey) {
            case 'mission-control':
                this.showMissionControl();
                break;
            case 'space-domain':
                this.showSpaceDomainAwareness();
                break;
            case 'learning':
                this.showLearningCenter();
                break;
            case 'data-sources':
                this.showDataSources();
                break;
            case 'mission-support':
                this.showMissionSupport();
                break;
            case 'settings':
                this.showSettings();
                break;
        }
    }

    getSectionSubtitle(sectionKey) {
        const subtitles = {
            'mission-control': `Welcome back, ${this.mainInterface.user?.name || 'Commander'}`,
            'space-domain': 'Monitoring 58,247 tracked objects in real-time',
            'learning': 'Interactive space operations training',
            'data-sources': 'Configure data feeds and integrations',
            'mission-support': 'Help center, documentation & support tickets',
            'settings': 'System preferences and account management'
        };
        
        return subtitles[sectionKey] || '';
    }

    async showMissionControl() {
        const contentArea = document.getElementById('content-area');
        
        // Import and initialize the enhanced mission control
        const { createMissionControlEnhanced } = await import('./mission-control-enhanced.js');
        this.mainInterface.missionControl = createMissionControlEnhanced(contentArea);
        
        // Make it available globally for navigation
        window.missionControlEnhanced = this.mainInterface.missionControl;
    }

    async showSpaceDomainAwareness() {
        // Lazy load the SDA interface module
        if (!this.mainInterface.sdaInterface) {
            const { SDAInterface } = await import('./sda-interface.js');
            this.mainInterface.sdaInterface = new SDAInterface(this.mainInterface);
        }
        
        const contentArea = document.getElementById('content-area');
        contentArea.innerHTML = this.mainInterface.sdaInterface.createInterface();
        
        // Move canvas to SDA view and ensure Earth is visible
        this.mainInterface.sdaInterface.moveCanvasToContainer();
        this.mainInterface.uiModule.hideOldUI();
        this.mainInterface.sdaInterface.setupHandlers();
        await this.mainInterface.sdaInterface.initializeVisualization();
    }

    async showLearningCenter() {
        // Open the training center in a new tab
        window.open('./training-center.html', '_blank');
    }

    showDataSources() {
        const contentArea = document.getElementById('content-area');
        contentArea.innerHTML = `
            <div class="data-sources-dashboard" style="padding: 24px;">
                <div class="data-header" style="margin-bottom: 32px;">
                    <h2 style="color: #e2e8f0; font-size: 24px; font-weight: 600; margin: 0 0 8px 0;">Data Integration Hub</h2>
                    <p style="color: #94a3b8; font-size: 14px; margin: 0;">Configure telemetry feeds, upload satellite data, and manage integrations</p>
                </div>
                
                <div class="data-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px;">
                    <!-- Upload Satellite Data -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="missionInterface.uploadSatelliteData()"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(0, 212, 255, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(0, 153, 255, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/sat.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">Upload Satellites</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Import TLE/NORAD data or manually create satellites</p>
                        <div style="color: #00d4ff; font-size: 13px; font-weight: 500;">Click to upload →</div>
                    </div>
                    
                    <!-- Live Data Feeds -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="missionInterface.showLiveDataFeeds()"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(16, 185, 129, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(16, 185, 129, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/telem.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">Live Data Feeds</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Connect to real-time telemetry streams</p>
                        <div style="color: #10b981; font-size: 13px; font-weight: 500;">Configure feeds →</div>
                    </div>
                    
                    <!-- API Integration -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="window.showMRNotification && window.showMRNotification('API Integration - Coming Soon', 'info')"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(251, 191, 36, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(251, 191, 36, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/3d.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">API Integration</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Connect external APIs for automated data ingestion</p>
                        <div style="color: #fbbf24; font-size: 13px; font-weight: 500;">Coming Soon</div>
                    </div>
                    
                    <!-- Ground Station Network -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="missionInterface.uploadGroundStations()"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(168, 85, 247, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(168, 85, 247, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/antenna.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">Ground Stations</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Configure and manage ground station network</p>
                        <div style="color: #a855f7; font-size: 13px; font-weight: 500;">Manage network →</div>
                    </div>
                    
                    <!-- Data Source Manager -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="missionInterface.openDataManager()"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(239, 68, 68, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(239, 68, 68, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/telem.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">Data Source Manager</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Advanced telemetry configuration</p>
                        <div style="color: #ef4444; font-size: 13px; font-weight: 500;">Open manager →</div>
                    </div>
                    
                    <!-- Export Data -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="window.showMRNotification && window.showMRNotification('Export functionality coming soon', 'info')"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(59, 130, 246, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(59, 130, 246, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/support.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">Export Data</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Export telemetry and orbital data</p>
                        <div style="color: #3b82f6; font-size: 13px; font-weight: 500;">Coming Soon</div>
                    </div>
                </div>
                
                <!-- Data Stats -->
                <div style="margin-top: 40px; padding: 24px; background: rgba(15, 23, 42, 0.4); border: 1px solid rgba(148, 163, 184, 0.1); border-radius: 16px;">
                    <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0 0 20px 0;">Data Overview</h3>
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px;">
                        <div>
                            <div style="color: #64748b; font-size: 13px; margin-bottom: 4px;">Active Data Sources</div>
                            <div style="color: #e2e8f0; font-size: 24px; font-weight: 600;">3</div>
                        </div>
                        <div>
                            <div style="color: #64748b; font-size: 13px; margin-bottom: 4px;">Data Points / Sec</div>
                            <div style="color: #10b981; font-size: 24px; font-weight: 600;">1,247</div>
                        </div>
                        <div>
                            <div style="color: #64748b; font-size: 13px; margin-bottom: 4px;">Tracked Objects</div>
                            <div style="color: #3b82f6; font-size: 24px; font-weight: 600;">58,247</div>
                        </div>
                        <div>
                            <div style="color: #64748b; font-size: 13px; margin-bottom: 4px;">Ground Stations</div>
                            <div style="color: #a855f7; font-size: 24px; font-weight: 600;">147</div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    async showMissionSupport() {
        const contentArea = document.getElementById('content-area');
        
        contentArea.innerHTML = `
            <div style="height: 100%; overflow-y: auto; padding: 24px;">
                <div style="max-width: 1400px; margin: 0 auto;">
                    <!-- Header -->
                    <div style="margin-bottom: 32px;">
                        <h2 style="color: #e2e8f0; font-size: 28px; font-weight: 600; margin-bottom: 8px;">Mission Support Center</h2>
                        <p style="color: #94a3b8; font-size: 16px;">Get help, access documentation, and manage support tickets</p>
                    </div>
                    
                    <!-- Quick Actions -->
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; margin-bottom: 32px;">
                        <div style="background: linear-gradient(135deg, #00d4ff 0%, #0099ff 100%); border-radius: 12px; padding: 24px; cursor: pointer; transition: all 0.3s;"
                             onclick="missionInterface.showNewTicketForm()"
                             onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 8px 24px rgba(0, 212, 255, 0.3)';"
                             onmouseout="this.style.transform=''; this.style.boxShadow='';">
                            <div style="display: flex; align-items: center; gap: 16px;">
                                <img src="assets/support.svg" style="width: 32px; height: 32px; filter: brightness(0) invert(1);" />
                                <div>
                                    <h3 style="color: white; font-size: 18px; font-weight: 600; margin-bottom: 4px;">Create Support Ticket</h3>
                                    <p style="color: rgba(255, 255, 255, 0.9); font-size: 14px;">Get help from our mission support team</p>
                                </div>
                            </div>
                        </div>
                        
                        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 12px; padding: 24px; cursor: pointer; transition: all 0.3s;"
                             onclick="window.open('https://docs.missionready.space', '_blank')"
                             onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(0, 212, 255, 0.3)';"
                             onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                            <div style="display: flex; align-items: center; gap: 16px;">
                                <img src="assets/learn.svg" style="width: 32px; height: 32px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                                <div>
                                    <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin-bottom: 4px;">Documentation</h3>
                                    <p style="color: #94a3b8; font-size: 14px;">API references, guides, and tutorials</p>
                                </div>
                            </div>
                        </div>
                        
                        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 12px; padding: 24px; cursor: pointer; transition: all 0.3s;"
                             onclick="missionInterface.showFAQs()"
                             onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(16, 185, 129, 0.3)';"
                             onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                            <div style="display: flex; align-items: center; gap: 16px;">
                                <img src="assets/3d.svg" style="width: 32px; height: 32px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                                <div>
                                    <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin-bottom: 4px;">FAQs</h3>
                                    <p style="color: #94a3b8; font-size: 14px;">Frequently asked questions</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Active Tickets -->
                    <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px;">
                        <h3 style="color: #e2e8f0; font-size: 20px; font-weight: 600; margin-bottom: 20px; display: flex; align-items: center; gap: 12px;">
                            <img src="assets/support.svg" style="width: 20px; height: 20px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            Your Support Tickets
                        </h3>
                        
                        <div id="support-tickets-list">
                            <!-- Tickets will be loaded here -->
                            <div style="text-align: center; padding: 60px 20px; color: #64748b;">
                                <img src="assets/support.svg" style="width: 48px; height: 48px; opacity: 0.3; margin-bottom: 16px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                                <p style="font-size: 16px;">No support tickets yet</p>
                                <p style="font-size: 14px; margin-top: 8px;">Create a ticket to get help from our team</p>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Contact Information -->
                    <div style="margin-top: 32px; padding: 20px; background: rgba(30, 41, 59, 0.3); border-radius: 12px; text-align: center;">
                        <p style="color: #94a3b8; font-size: 14px; margin-bottom: 8px;">For urgent issues, contact us directly:</p>
                        <a href="mailto:mission_support@cyberrts.com" style="color: #00d4ff; text-decoration: none; font-size: 16px; font-weight: 500;">mission_support@cyberrts.com</a>
                    </div>
                </div>
            </div>
        `;
        
        // Load tickets
        this.mainInterface.loadSupportTickets();
    }

    showSettings() {
        const contentArea = document.getElementById('content-area');
        contentArea.innerHTML = `
            <div class="coming-soon-container">
                <h2>System Settings</h2>
                <p>Configure your preferences and manage your account.</p>
                <p class="coming-soon">Feature coming soon...</p>
            </div>
        `;
    }

    // Legacy compatibility methods
    moveCanvasToSDAContainer() {
        if (this.mainInterface.sdaInterface) {
            this.mainInterface.sdaInterface.moveCanvasToContainer();
        }
    }
    
    setupSDAHandlers() {
        if (this.mainInterface.sdaInterface) {
            this.mainInterface.sdaInterface.setupHandlers();
        }
    }
    
    initializeSDAVisualization() {
        if (this.mainInterface.sdaInterface) {
            this.mainInterface.sdaInterface.initializeVisualization();
        }
    }
}